import { useState } from 'react';
import { api, ApiError, formatDate, scr } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { useRefData } from '../../lib/refdata';
import type { Asset, AssetRegister, StockItem } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { EmptyState, ErrorNote, Field, Modal, PageHeader, Spinner, StatCard, StatusBadge } from '../../ui/components';

export function Assets() {
  const { persona } = useAuth();
  const { votes, voteByCode } = useRefData();
  const vote = voteByCode(persona?.voteCode);

  const isAssetOfficer = (persona?.roles.includes('MDA_ASSET_OFFICER') || persona?.roles.includes('MOF_ACCOUNTANT_GENERAL')) ?? false;
  const isAccountantGeneral = persona?.roles.includes('MOF_ACCOUNTANT_GENERAL') ?? false;
  const isSurvey = (persona?.roles.includes('BOARD_OF_SURVEY') || persona?.roles.includes('MOF_ACCOUNTANT_GENERAL')) ?? false;

  const [showRegister, setShowRegister] = useState(false);
  const [showPolicy, setShowPolicy] = useState(false);
  const [showStock, setShowStock] = useState(false);
  const [transferTarget, setTransferTarget] = useState<Asset | null>(null);
  const [disposeTarget, setDisposeTarget] = useState<Asset | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [stockItems, setStockItems] = useState<StockItem[]>(() => {
    const raw = sessionStorage.getItem('govfin.stockItems');
    return raw ? (JSON.parse(raw) as StockItem[]) : [];
  });

  const registerQ = useApiQuery<AssetRegister>(vote ? `/api/v1/assets/register?voteId=${vote.id}` : '/api/v1/assets/register');
  const exceptionsQ = useApiQuery<{ items: Asset[] }>(isAssetOfficer ? '/api/v1/assets/verification-exceptions' : null);

  const persistStock = (items: StockItem[]) => {
    setStockItems(items);
    sessionStorage.setItem('govfin.stockItems', JSON.stringify(items));
  };

  const confirmCustodian = async (a: Asset) => {
    setActionError(null);
    try {
      await api.post(`/api/v1/assets/${a.id}/custodian-confirm`);
      registerQ.reload();
    } catch (e) {
      setActionError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Confirmation failed');
    }
  };

  const verifyScan = async (a: Asset) => {
    const location = window.prompt(`Confirm the current location of ${a.tagCode}:`, a.location);
    if (!location) return;
    setActionError(null);
    try {
      await api.post('/api/v1/assets/verify-scan', { tagCode: a.tagCode, location });
      registerQ.reload();
      exceptionsQ.reload();
    } catch (e) {
      setActionError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Verification failed');
    }
  };

  const toSurvey = async (a: Asset) => {
    setActionError(null);
    try {
      await api.post(`/api/v1/assets/${a.id}/to-survey`);
      registerQ.reload();
    } catch (e) {
      setActionError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to route to board of survey');
    }
  };

  const inUse = (registerQ.data?.items ?? []).filter((a) => a.status === 'IN_USE').length;

  return (
    <div>
      <PageHeader
        title="Fixed Assets"
        subtitle="What this ministry owns, where it is, and what it's worth — asset shells confirm to a custodian, the board of survey disposes"
        actions={
          isAssetOfficer ? (
            <div className="flex gap-2">
              <button className="btn-secondary" onClick={() => setShowStock(true)}>
                + Consumables item
              </button>
              {isAccountantGeneral ? (
                <button className="btn-secondary" onClick={() => setShowPolicy(true)}>
                  Depreciation policy
                </button>
              ) : null}
              <button className="btn-primary" onClick={() => setShowRegister(true)}>
                + Register asset
              </button>
            </div>
          ) : undefined
        }
      />
      {actionError ? (
        <div className="mb-4">
          <ErrorNote message={actionError} />
        </div>
      ) : null}

      {registerQ.data ? (
        <div className="mb-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard label="Total cost" value={scr(registerQ.data.totals.cost)} hint={`${registerQ.data.items.length} asset(s)`} />
          <StatCard label="Net book value" value={scr(registerQ.data.totals.bookValue)} tone="good" />
          <StatCard label="In use" value={inUse} />
          <StatCard
            label="Verification exceptions"
            value={exceptionsQ.data?.items.length ?? '—'}
            tone={(exceptionsQ.data?.items.length ?? 0) > 0 ? 'warn' : 'default'}
            hint="Not verified in the last 12 months"
          />
        </div>
      ) : null}

      <div className="mb-6">
        <h2 className="mb-2 text-sm font-bold text-slate-700">Asset register</h2>
        <div className="card overflow-x-auto">
          {registerQ.loading ? (
            <Spinner />
          ) : registerQ.error ? (
            <ErrorNote message={registerQ.error} />
          ) : (registerQ.data?.items.length ?? 0) === 0 ? (
            <EmptyState title="No assets registered" hint="Capital vouchers create asset shells automatically, or register legacy stock-take items here." />
          ) : (
            <table className="w-full">
              <thead>
                <tr>
                  <th className="table-head">Tag</th>
                  <th className="table-head">Class</th>
                  <th className="table-head">Description</th>
                  <th className="table-head">Location</th>
                  <th className="table-head text-right">Cost</th>
                  <th className="table-head text-right">Book value</th>
                  <th className="table-head">Status</th>
                  {isAssetOfficer || isSurvey ? <th className="table-head" /> : null}
                </tr>
              </thead>
              <tbody>
                {registerQ.data!.items.map((a) => (
                  <tr key={a.id} className="hover:bg-slate-50">
                    <td className="table-cell font-medium">{a.tagCode}</td>
                    <td className="table-cell text-xs">{a.class}</td>
                    <td className="table-cell max-w-xs truncate" title={a.description}>
                      {a.description}
                    </td>
                    <td className="table-cell text-xs">{a.location}</td>
                    <td className="table-cell text-right tabular-nums">{scr(a.cost)}</td>
                    <td className="table-cell text-right tabular-nums">{a.bookValue !== undefined ? scr(a.bookValue) : '—'}</td>
                    <td className="table-cell">
                      <StatusBadge status={a.status} />
                      <div className="mt-0.5 text-[11px] text-slate-400">{a.lastVerifiedAt ? `verified ${formatDate(a.lastVerifiedAt)}` : 'never verified'}</div>
                    </td>
                    {isAssetOfficer || isSurvey ? (
                      <td className="table-cell text-right">
                        <div className="flex justify-end gap-1.5">
                          {isAssetOfficer && a.status === 'PENDING_CUSTODIAN' ? (
                            <button className="btn-success !px-2.5 !py-1 text-xs" onClick={() => confirmCustodian(a)}>
                              Confirm custodian
                            </button>
                          ) : null}
                          {isAssetOfficer && a.status === 'IN_USE' ? (
                            <>
                              <button className="btn-secondary !px-2.5 !py-1 text-xs" onClick={() => verifyScan(a)}>
                                Verify
                              </button>
                              <button className="btn-secondary !px-2.5 !py-1 text-xs" onClick={() => setTransferTarget(a)}>
                                Transfer
                              </button>
                              <button className="btn-danger !px-2.5 !py-1 text-xs" onClick={() => toSurvey(a)}>
                                To survey
                              </button>
                            </>
                          ) : null}
                          {isSurvey && a.status === 'BOARD_OF_SURVEY' ? (
                            <button className="btn-danger !px-2.5 !py-1 text-xs" onClick={() => setDisposeTarget(a)}>
                              Dispose
                            </button>
                          ) : null}
                        </div>
                      </td>
                    ) : null}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {isAssetOfficer && (exceptionsQ.data?.items.length ?? 0) > 0 ? (
        <div className="mb-6">
          <h2 className="mb-2 text-sm font-bold text-slate-700">Overdue verifications</h2>
          <div className="card overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr>
                  <th className="table-head">Tag</th>
                  <th className="table-head">Description</th>
                  <th className="table-head">Location</th>
                </tr>
              </thead>
              <tbody>
                {exceptionsQ.data!.items.map((a) => (
                  <tr key={a.id}>
                    <td className="table-cell font-medium">{a.tagCode}</td>
                    <td className="table-cell">{a.description}</td>
                    <td className="table-cell text-xs">{a.location}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}

      {isAssetOfficer ? (
        <div className="mb-6">
          <h2 className="mb-2 text-sm font-bold text-slate-700">Consumables store (this session)</h2>
          <p className="mb-2 text-xs text-slate-400">There is no stock-listing endpoint yet — items registered in this tab are tracked here.</p>
          <div className="card overflow-x-auto">
            {stockItems.length === 0 ? (
              <EmptyState title="No stock items yet" />
            ) : (
              <table className="w-full">
                <thead>
                  <tr>
                    <th className="table-head">SKU</th>
                    <th className="table-head">Name</th>
                    <th className="table-head text-right">On hand</th>
                    <th className="table-head text-right">Reorder level</th>
                    <th className="table-head" />
                  </tr>
                </thead>
                <tbody>
                  {stockItems.map((s) => (
                    <tr key={s.id}>
                      <td className="table-cell font-medium">{s.sku}</td>
                      <td className="table-cell">{s.name}</td>
                      <td className="table-cell text-right tabular-nums">{s.quantity}</td>
                      <td className="table-cell text-right tabular-nums">{s.reorderLevel}</td>
                      <td className="table-cell text-right">
                        <button
                          className="btn-secondary !px-2.5 !py-1 text-xs"
                          onClick={async () => {
                            const deltaRaw = window.prompt('Quantity change (+ receipt, − issue):', '-1');
                            if (!deltaRaw) return;
                            const reason = window.prompt('Reason:') ?? '';
                            const updated = await api.post<{ quantity: string }>(`/api/v1/assets/stock/${s.id}/move`, {
                              delta: Number.parseFloat(deltaRaw),
                              reason: reason || 'Adjustment',
                            });
                            persistStock(stockItems.map((x) => (x.id === s.id ? { ...x, quantity: updated.quantity } : x)));
                          }}
                        >
                          Move stock
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      ) : null}

      {showRegister && vote ? (
        <RegisterAssetModal
          voteId={vote.id}
          onClose={() => setShowRegister(false)}
          onDone={() => {
            setShowRegister(false);
            registerQ.reload();
          }}
        />
      ) : null}
      {showPolicy ? (
        <DepreciationPolicyModal onClose={() => setShowPolicy(false)} onDone={() => setShowPolicy(false)} />
      ) : null}
      {showStock && vote ? (
        <StockItemModal
          voteId={vote.id}
          onClose={() => setShowStock(false)}
          onDone={(item) => {
            setShowStock(false);
            persistStock([item, ...stockItems]);
          }}
        />
      ) : null}
      {transferTarget ? (
        <TransferModal
          asset={transferTarget}
          votes={votes}
          onClose={() => setTransferTarget(null)}
          onDone={() => {
            setTransferTarget(null);
            registerQ.reload();
          }}
        />
      ) : null}
      {disposeTarget ? (
        <DisposeModal
          asset={disposeTarget}
          onClose={() => setDisposeTarget(null)}
          onDone={() => {
            setDisposeTarget(null);
            registerQ.reload();
          }}
        />
      ) : null}
    </div>
  );
}

function RegisterAssetModal({ voteId, onClose, onDone }: { voteId: string; onClose: () => void; onDone: () => void }) {
  const [form, setForm] = useState({ class: 'EQUIPMENT' as Asset['class'], description: '', cost: '', location: '' });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/v1/assets/', {
        class: form.class,
        description: form.description,
        cost: Number.parseFloat(form.cost),
        custodianVoteId: voteId,
        location: form.location,
      });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to register asset');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Register asset (legacy stock-take)" onClose={onClose}>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <Field label="Class">
          <select className="field-input" value={form.class} onChange={(e) => setForm({ ...form, class: e.target.value as Asset['class'] })}>
            <option value="VEHICLE">Vehicle</option>
            <option value="BUILDING">Building</option>
            <option value="EQUIPMENT">Equipment</option>
            <option value="ICT">ICT</option>
            <option value="FURNITURE">Furniture</option>
          </select>
        </Field>
        <Field label="Description">
          <input className="field-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Cost (SCR)">
            <input className="field-input" type="number" min="0" step="0.01" value={form.cost} onChange={(e) => setForm({ ...form, cost: e.target.value })} />
          </Field>
          <Field label="Location">
            <input className="field-input" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="e.g. Head office, Victoria" />
          </Field>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" onClick={submit} disabled={busy || form.description.length < 3 || !form.cost || !form.location}>
            {busy ? 'Registering…' : 'Register asset'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function DepreciationPolicyModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [assetClass, setAssetClass] = useState<Asset['class']>('EQUIPMENT');
  const [ratePercent, setRatePercent] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/v1/assets/depreciation-policies', { class: assetClass, method: 'STRAIGHT_LINE', ratePercent: Number.parseFloat(ratePercent) });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to set policy');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Set depreciation policy" onClose={onClose}>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <Field label="Asset class">
          <select className="field-input" value={assetClass} onChange={(e) => setAssetClass(e.target.value as Asset['class'])}>
            <option value="VEHICLE">Vehicle</option>
            <option value="BUILDING">Building</option>
            <option value="EQUIPMENT">Equipment</option>
            <option value="ICT">ICT</option>
            <option value="FURNITURE">Furniture</option>
          </select>
        </Field>
        <Field label="Straight-line rate % per annum">
          <input className="field-input" type="number" min="0" max="50" step="0.1" value={ratePercent} onChange={(e) => setRatePercent(e.target.value)} />
        </Field>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" onClick={submit} disabled={busy || !ratePercent}>
            {busy ? 'Saving…' : 'Set policy'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function StockItemModal({ voteId, onClose, onDone }: { voteId: string; onClose: () => void; onDone: (item: StockItem) => void }) {
  const [sku, setSku] = useState('');
  const [name, setName] = useState('');
  const [quantity, setQuantity] = useState('');
  const [reorderLevel, setReorderLevel] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const item = await api.post<StockItem>('/api/v1/assets/stock', {
        storeVoteId: voteId,
        sku,
        name,
        quantity: Number.parseFloat(quantity),
        reorderLevel: Number.parseFloat(reorderLevel),
      });
      onDone(item);
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to create stock item');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="New consumables store item" onClose={onClose}>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <div className="grid grid-cols-2 gap-3">
          <Field label="SKU">
            <input className="field-input" value={sku} onChange={(e) => setSku(e.target.value)} placeholder="e.g. STAT-A4-500" />
          </Field>
          <Field label="Name">
            <input className="field-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. A4 paper (ream)" />
          </Field>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Opening quantity">
            <input className="field-input" type="number" min="0" value={quantity} onChange={(e) => setQuantity(e.target.value)} />
          </Field>
          <Field label="Reorder level">
            <input className="field-input" type="number" min="0" value={reorderLevel} onChange={(e) => setReorderLevel(e.target.value)} />
          </Field>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" onClick={submit} disabled={busy || !sku || !name || !quantity || !reorderLevel}>
            {busy ? 'Creating…' : 'Create item'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function TransferModal({
  asset,
  votes,
  onClose,
  onDone,
}: {
  asset: Asset;
  votes: Array<{ id: string; code: string; name: string }>;
  onClose: () => void;
  onDone: () => void;
}) {
  const [toVote, setToVote] = useState('');
  const [location, setLocation] = useState(asset.location);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/v1/assets/${asset.id}/transfer`, { toVote, location });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Transfer failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={`Transfer ${asset.tagCode}`} onClose={onClose}>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <Field label="To vote (custodian ministry)">
          <select className="field-input" value={toVote} onChange={(e) => setToVote(e.target.value)}>
            <option value="">Select vote…</option>
            {votes.map((v) => (
              <option key={v.id} value={v.id}>
                {v.code} — {v.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label="New location">
          <input className="field-input" value={location} onChange={(e) => setLocation(e.target.value)} />
        </Field>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" onClick={submit} disabled={busy || !toVote || location.length < 2}>
            {busy ? 'Transferring…' : 'Transfer'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function DisposeModal({ asset, onClose, onDone }: { asset: Asset; onClose: () => void; onDone: () => void }) {
  const [method, setMethod] = useState<'AUCTION' | 'WRITEOFF'>('WRITEOFF');
  const [proceedsReceiptId, setProceedsReceiptId] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/v1/assets/${asset.id}/dispose`, {
        method,
        ...(method === 'AUCTION' ? { proceedsReceiptId } : {}),
      });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Disposal failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={`Dispose — ${asset.tagCode}`} onClose={onClose}>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <Field label="Method">
          <select className="field-input" value={method} onChange={(e) => setMethod(e.target.value as 'AUCTION' | 'WRITEOFF')}>
            <option value="WRITEOFF">Write-off</option>
            <option value="AUCTION">Auction</option>
          </select>
        </Field>
        {method === 'AUCTION' ? (
          <Field label="F07 proceeds receipt ID">
            <input className="field-input" value={proceedsReceiptId} onChange={(e) => setProceedsReceiptId(e.target.value)} />
          </Field>
        ) : null}
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-danger" onClick={submit} disabled={busy || (method === 'AUCTION' && !proceedsReceiptId)}>
            {busy ? 'Disposing…' : 'Confirm disposal'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
